// Validation utilities for Indian business requirements

// Indian state codes for GST validation
const GST_STATE_CODES = {
  '01': 'Jammu and Kashmir',
  '02': 'Himachal Pradesh',
  '03': 'Punjab',
  '04': 'Chandigarh',
  '05': 'Uttarakhand',
  '06': 'Haryana',
  '07': 'Delhi',
  '08': 'Rajasthan',
  '09': 'Uttar Pradesh',
  '10': 'Bihar',
  '11': 'Sikkim',
  '12': 'Arunachal Pradesh',
  '13': 'Nagaland',
  '14': 'Manipur',
  '15': 'Mizoram',
  '16': 'Tripura',
  '17': 'Meghalaya',
  '18': 'Assam',
  '19': 'West Bengal',
  '20': 'Jharkhand',
  '21': 'Odisha',
  '22': 'Chhattisgarh',
  '23': 'Madhya Pradesh',
  '24': 'Gujarat',
  '25': 'Daman and Diu',
  '26': 'Dadra and Nagar Haveli',
  '27': 'Maharashtra',
  '28': 'Andhra Pradesh',
  '29': 'Karnataka',
  '30': 'Goa',
  '31': 'Lakshadweep',
  '32': 'Kerala',
  '33': 'Tamil Nadu',
  '34': 'Puducherry',
  '35': 'Andaman and Nicobar Islands',
  '36': 'Telangana',
  '37': 'Andhra Pradesh (New)',
  '38': 'Ladakh'
};

/**
 * Validates GST Identification Number (GSTIN) according to Indian standards
 * Format: 15 characters - AABBBBBBBBBCDEZ
 * Where:
 * AA = State code (01-38)
 * BBBBBBBBB = Entity PAN (10 characters)
 * C = Entity number (1-9, A-Z)
 * D = Check digit (calculated)
 * E = Usually 'Z'
 * Z = Extra digit (0-9, A-Z)
 */
export const validateGSTIN = (gstin) => {
  if (!gstin) {
    return { isValid: true, error: null }; // GST is optional
  }

  // Remove spaces and convert to uppercase
  const cleanGSTIN = gstin.replace(/\s/g, '').toUpperCase();

  // Check length
  if (cleanGSTIN.length !== 15) {
    return {
      isValid: false,
      error: 'GSTIN must be exactly 15 characters long'
    };
  }

  // Check format using regex
  const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;
  if (!gstinRegex.test(cleanGSTIN)) {
    return {
      isValid: false,
      error: 'Invalid GSTIN format. Format: 22AAAAA0000A1Z5'
    };
  }

  // Validate state code
  const stateCode = cleanGSTIN.substring(0, 2);
  if (!GST_STATE_CODES[stateCode]) {
    return {
      isValid: false,
      error: `Invalid state code: ${stateCode}. Must be between 01-38`
    };
  }

  // Validate PAN part (positions 2-11)
  const panPart = cleanGSTIN.substring(2, 12);
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  if (!panRegex.test(panPart)) {
    return {
      isValid: false,
      error: 'Invalid PAN section in GSTIN'
    };
  }

  // Validate check digit (simplified - actual calculation is complex)
  const entityCode = cleanGSTIN.charAt(12);
  if (!/^[1-9A-Z]$/.test(entityCode)) {
    return {
      isValid: false,
      error: 'Invalid entity code (13th character)'
    };
  }

  // Check if 14th character is 'Z' (most common case)
  const fourteenthChar = cleanGSTIN.charAt(14);
  if (fourteenthChar !== 'Z') {
    return {
      isValid: true, // Valid but unusual
      warning: `Unusual GSTIN format: 14th character is '${fourteenthChar}' instead of 'Z'`
    };
  }

  return {
    isValid: true,
    error: null,
    stateCode: stateCode,
    stateName: GST_STATE_CODES[stateCode],
    panPart: panPart
  };
};

/**
 * Validates PAN (Permanent Account Number) according to Indian standards
 * Format: AAAAA9999A
 * Where A = Letter, 9 = Digit
 */
export const validatePAN = (pan) => {
  if (!pan) {
    return { isValid: true, error: null }; // PAN is optional
  }

  // Remove spaces and convert to uppercase
  const cleanPAN = pan.replace(/\s/g, '').toUpperCase();

  // Check length
  if (cleanPAN.length !== 10) {
    return {
      isValid: false,
      error: 'PAN must be exactly 10 characters long'
    };
  }

  // Check format
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  if (!panRegex.test(cleanPAN)) {
    return {
      isValid: false,
      error: 'Invalid PAN format. Format: AAAAA9999A'
    };
  }

  // Validate 4th character (category)
  const category = cleanPAN.charAt(3);
  const validCategories = ['A', 'B', 'C', 'F', 'G', 'H', 'L', 'J', 'P', 'T'];
  if (!validCategories.includes(category)) {
    return {
      isValid: false,
      error: `Invalid PAN category code: ${category}`
    };
  }

  return {
    isValid: true,
    error: null,
    category: category
  };
};

/**
 * Validates Indian mobile number
 */
export const validateIndianMobile = (mobile) => {
  if (!mobile) {
    return { isValid: true, error: null };
  }

  // Remove spaces, dashes, and brackets
  const cleanMobile = mobile.replace(/[\s\-\(\)]/g, '');

  // Check if it starts with +91
  let mobileNumber = cleanMobile;
  if (mobileNumber.startsWith('+91')) {
    mobileNumber = mobileNumber.substring(3);
  } else if (mobileNumber.startsWith('91') && mobileNumber.length === 12) {
    mobileNumber = mobileNumber.substring(2);
  }

  // Check length
  if (mobileNumber.length !== 10) {
    return {
      isValid: false,
      error: 'Mobile number must be 10 digits'
    };
  }

  // Check if all digits
  if (!/^\d{10}$/.test(mobileNumber)) {
    return {
      isValid: false,
      error: 'Mobile number must contain only digits'
    };
  }

  // Check if starts with valid digit (6-9)
  const firstDigit = mobileNumber.charAt(0);
  if (!['6', '7', '8', '9'].includes(firstDigit)) {
    return {
      isValid: false,
      error: 'Mobile number must start with 6, 7, 8, or 9'
    };
  }

  return {
    isValid: true,
    error: null,
    formatted: `+91 ${mobileNumber}`
  };
};

/**
 * Validates Indian PIN code
 */
export const validatePinCode = (pinCode) => {
  if (!pinCode) {
    return { isValid: true, error: null };
  }

  const cleanPinCode = pinCode.replace(/\s/g, '');

  if (!/^\d{6}$/.test(cleanPinCode)) {
    return {
      isValid: false,
      error: 'PIN code must be exactly 6 digits'
    };
  }

  // Check if it starts with 0 (which is generally invalid)
  if (cleanPinCode.startsWith('0')) {
    return {
      isValid: false,
      error: 'PIN code cannot start with 0'
    };
  }

  return {
    isValid: true,
    error: null
  };
};

/**
 * Validates email format
 */
export const validateEmail = (email) => {
  if (!email) {
    return { isValid: true, error: null };
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return {
      isValid: false,
      error: 'Invalid email format'
    };
  }

  return {
    isValid: true,
    error: null
  };
};

/**
 * Cross-validation: Check if PAN in GSTIN matches provided PAN
 */
export const validateGSTINWithPAN = (gstin, pan) => {
  if (!gstin || !pan) {
    return { isValid: true, error: null };
  }

  const gstinValidation = validateGSTIN(gstin);
  const panValidation = validatePAN(pan);

  if (!gstinValidation.isValid) {
    return gstinValidation;
  }

  if (!panValidation.isValid) {
    return panValidation;
  }

  // Extract PAN from GSTIN and compare
  const gstinPAN = gstin.substring(2, 12).toUpperCase();
  const providedPAN = pan.replace(/\s/g, '').toUpperCase();

  if (gstinPAN !== providedPAN) {
    return {
      isValid: false,
      error: 'PAN number does not match with GSTIN. GSTIN contains different PAN.'
    };
  }

  return {
    isValid: true,
    error: null
  };
};

export default {
  validateGSTIN,
  validatePAN,
  validateIndianMobile,
  validatePinCode,
  validateEmail,
  validateGSTINWithPAN
}; 